/**
 * TimesheetRepository wraps the backend timesheet endpoints.
 */
import { Timesheet } from './timesheetModel';
import { post } from '../../../shared/api/post';
import { Timesheet_Endpoints } from '../../../shared/constants';

export const TimesheetRepository = {
  /**
   * Fetch timesheet lines, optionally filtered by project or task and a date range.
   *
   * A task wins over a project, since the task already sits inside one.
   * Filters left empty are not sent, so the backend returns its default scope.
   *
   * @param {{projectId?:number|string,taskId?:number|string,dateFrom?:string,dateTo?:string,accessToken?:string|null}} params
   * @returns {Promise<{success:boolean,total:number,timesheets:Timesheet[]}>}
   * @throws {Error} When the backend response is not successful.
   */
  listTimesheets: async ({ projectId, taskId, dateFrom, dateTo, accessToken } = {}) => {
    const data = await post(
      Timesheet_Endpoints.listTimesheets,
      {
        ...(taskId ? { task_id: Number(taskId) } : projectId ? { project_id: Number(projectId) } : {}),
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      },
      accessToken,
    );

    const rows = Array.isArray(data.items) ? data.items : [];
    console.log('[timesheet] parsed rows', rows.length, 'of total', data.total);

    return {
      success: true,
      total: data.total ?? rows.length,
      timesheets: rows.map((row) => Timesheet.fromJson(row)),
    };
  },

  /**
   * Create a timesheet line. Attachments are not supported.
   *
   * @param {{taskId:number|string,date:string,timeSpent:string,description?:string,accessToken?:string|null}} params
   *   `timeSpent` is "HH:MM"; the backend derives project and employee itself.
   * @returns {Promise<Timesheet>} The created line.
   * @throws {Error} When the backend response is not successful.
   */
  createTimesheet: async ({ taskId, date, timeSpent, description, accessToken } = {}) => {
    const data = await post(
      Timesheet_Endpoints.createTimesheet,
      {
        task_id: Number(taskId),
        date,
        time_spent: timeSpent,
        ...(description ? { description } : {}),
      },
      accessToken,
    );

    return Timesheet.fromJson(data.item ?? {});
  },

  /**
   * Update a timesheet line. Only time and description are editable; the
   * backend ignores attachments on this endpoint, so they are never sent.
   *
   * @param {{id:number|string,timeSpent:string,description?:string,accessToken?:string|null}} params
   * @returns {Promise<Timesheet>} The updated line.
   * @throws {Error} When the backend response is not successful.
   */
  updateTimesheet: async ({ id, timeSpent, description, accessToken } = {}) => {
    const data = await post(
      Timesheet_Endpoints.updateTimesheet,
      {
        id: Number(id),
        time_spent: timeSpent,
        description: description ?? '',
      },
      accessToken,
    );

    return Timesheet.fromJson(data.item ?? {});
  },

  /**
   * Delete a timesheet line.
   *
   * @param {{id:number|string,accessToken?:string|null}} params
   * @returns {Promise<boolean>} True once the backend confirms the deletion.
   * @throws {Error} When the backend response is not successful.
   */
  deleteTimesheet: async ({ id, accessToken } = {}) => {
    const data = await post(Timesheet_Endpoints.deleteTimesheet, { id: Number(id) }, accessToken);
    return data.deleted === true;
  },
};
